import React from 'react';
import { Download, Eye, ImagePlus, RefreshCw, Upload } from 'lucide-react';
import { firearmEvidenceService } from '../../services/firearmEvidenceService';
import {
  FIREARM_EVIDENCE_ALLOWED_MIME_TYPES,
  FIREARM_EVIDENCE_CATEGORY_OPTIONS,
  FIREARM_EVIDENCE_MAX_BYTES,
  FirearmEvidence,
  FirearmEvidenceCategory,
} from '../../types/firearmEvidence';
import DocumentPreviewModal from './DocumentPreviewModal';
import EmptyTableState from './EmptyTableState';
import TypeBadge from './TypeBadge';
import './FirearmEvidencePanel.css';

interface FirearmEvidencePanelProps {
  contextType: 'issuance' | 'return' | 'repair';
  contextId: number;
  firearmSerialNumber?: string | null;
  readOnly?: boolean;
  required?: boolean;
  allowedCategories?: FirearmEvidenceCategory[];
  title?: string;
  description?: string;
  className?: string;
  showTechnicalMetadata?: boolean;
  onUploadComplete?: (evidence: FirearmEvidence) => void;
}

interface StoredPreviewState {
  evidenceId: number;
  filename: string;
  contentType?: string;
  objectUrl?: string;
  loading: boolean;
  error?: string;
}

const allowedExtensions: Record<string, string[]> = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'image/webp': ['webp'],
};

const fileExtension = (filename: string): string => {
  const match = filename.toLowerCase().match(/\.([a-z0-9]+)$/);
  return match?.[1] || '';
};

const validateSelectedFile = (file: File): string => {
  if (file.size === 0) return 'Select a non-empty JPEG, PNG, or WebP image.';
  if (file.size > FIREARM_EVIDENCE_MAX_BYTES) return 'Firearm evidence must not exceed 5 MiB.';
  if (!FIREARM_EVIDENCE_ALLOWED_MIME_TYPES.includes(file.type as typeof FIREARM_EVIDENCE_ALLOWED_MIME_TYPES[number])) {
    return 'Only JPEG, PNG, or WebP firearm evidence is supported.';
  }
  const approvedExtensions = allowedExtensions[file.type] || [];
  if (!approvedExtensions.includes(fileExtension(file.name))) {
    return 'The image filename extension must match its JPEG, PNG, or WebP type.';
  }
  return '';
};

const formatBytes = (size: number): string => {
  if (!Number.isFinite(size) || size < 0) return 'Unknown size';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KiB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MiB`;
};

const formatDateTime = (value: string): string => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const firstResponseMessage = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstResponseMessage(item);
      if (message) return message;
    }
  }
  return '';
};

const responseDataMessage = (data: unknown): string => {
  const directMessage = firstResponseMessage(data);
  if (directMessage) return directMessage;
  if (!data || typeof data !== 'object') return '';

  const fields = data as Record<string, unknown>;
  const detailMessage = firstResponseMessage(fields.detail);
  if (detailMessage) return detailMessage;
  for (const [field, value] of Object.entries(fields)) {
    if (field === 'detail') continue;
    const message = firstResponseMessage(value);
    if (message) return message;
  }
  return '';
};

const responseMessageFromError = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: unknown }).response;
    if (
      response
      && typeof response === 'object'
      && 'status' in response
      && (response as { status?: unknown }).status === 400
      && 'data' in response
    ) {
      const backendMessage = responseDataMessage((response as { data?: unknown }).data);
      if (backendMessage) return backendMessage;
    }
  }
  return '';
};

const errorText = (error: unknown, fallback: string): string => {
  const directBackendMessage = responseMessageFromError(error);
  if (directBackendMessage) return directBackendMessage;
  if (error && typeof error === 'object' && 'originalError' in error) {
    const wrappedBackendMessage = responseMessageFromError(
      (error as { originalError?: unknown }).originalError,
    );
    if (wrappedBackendMessage) return wrappedBackendMessage;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message?: unknown }).message || '').trim();
    if (message) return message;
  }
  return fallback;
};

const safeDownloadName = (filename: string, evidenceId: number): string => {
  const basename = filename.split(/[\\/]/).pop() || '';
  const withoutControlCharacters = Array.from(basename)
    .filter((character) => {
      const codePoint = character.codePointAt(0) || 0;
      return codePoint > 31 && codePoint !== 127;
    })
    .join('');
  const cleaned = withoutControlCharacters
    .replace(/[^a-zA-Z0-9._() -]/g, '_')
    .replace(/^\.+/, '')
    .slice(0, 180);
  return cleaned || `firearm-evidence-${evidenceId}`;
};

const categoryLabel = (evidence: FirearmEvidence): string => (
  evidence.category_display
  || FIREARM_EVIDENCE_CATEGORY_OPTIONS.find((option) => option.value === evidence.category)?.label
  || evidence.category
);

const FirearmEvidencePanel: React.FC<FirearmEvidencePanelProps> = ({
  contextType,
  contextId,
  firearmSerialNumber,
  readOnly = true,
  required = false,
  allowedCategories,
  title,
  description = 'Authorized firearm condition images and integrity metadata.',
  className = '',
  showTechnicalMetadata = true,
  onUploadComplete,
}) => {
  const [records, setRecords] = React.useState<FirearmEvidence[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [downloadingId, setDownloadingId] = React.useState<number | null>(null);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = React.useState<string>();
  const [storedPreview, setStoredPreview] = React.useState<StoredPreviewState | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const loadTokenRef = React.useRef(0);
  const uploadTokenRef = React.useRef(0);
  const previewTokenRef = React.useRef(0);
  const storedPreviewUrlRef = React.useRef<string>();
  const headingId = React.useId();
  const fileHelpId = React.useId();

  const revokeStoredPreviewUrl = React.useCallback(() => {
    if (storedPreviewUrlRef.current) {
      URL.revokeObjectURL(storedPreviewUrlRef.current);
      storedPreviewUrlRef.current = undefined;
    }
  }, []);

  const closeStoredPreview = React.useCallback(() => {
    previewTokenRef.current += 1;
    revokeStoredPreviewUrl();
    setStoredPreview(null);
  }, [revokeStoredPreviewUrl]);

  const loadEvidence = React.useCallback(async () => {
    const token = ++loadTokenRef.current;
    if (!Number.isInteger(contextId) || contextId <= 0) {
      setRecords([]);
      setLoading(false);
      setError('Select a transaction before loading evidence.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = contextType === 'issuance'
        ? await firearmEvidenceService.listIssuanceEvidence(contextId)
        : await firearmEvidenceService.listReturnEvidence(contextId);
      if (token !== loadTokenRef.current) return;
      setRecords(allowedCategories?.length
        ? result.filter((record) => allowedCategories.includes(record.category))
        : result);
    } catch (loadError) {
      if (token !== loadTokenRef.current) return;
      setError(errorText(loadError, 'Stored firearm evidence could not be loaded.'));
    } finally {
      if (token === loadTokenRef.current) setLoading(false);
    }
  }, [allowedCategories, contextId, contextType]);

  React.useEffect(() => {
    void loadEvidence();
    return () => {
      loadTokenRef.current += 1;
    };
  }, [loadEvidence]);

  React.useEffect(() => {
    uploadTokenRef.current += 1;
    setUploading(false);
    setSelectedFile(null);
    setNotes('');
    setSuccess('');
    closeStoredPreview();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [closeStoredPreview, contextId, contextType]);

  React.useEffect(() => {
    if (!selectedFile) {
      setSelectedPreviewUrl(undefined);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setSelectedPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  React.useEffect(() => () => {
    uploadTokenRef.current += 1;
    previewTokenRef.current += 1;
    revokeStoredPreviewUrl();
  }, [revokeStoredPreviewUrl]);

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const selectFile = (file: File | null) => {
    setError('');
    setSuccess('');
    if (!file) {
      clearSelectedFile();
      return;
    }
    const validationError = validateSelectedFile(file);
    if (validationError) {
      clearSelectedFile();
      setError(validationError);
      return;
    }
    setSelectedFile(file);
  };

  const uploadEvidence = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!selectedFile) {
      setError('Select a firearm evidence image before uploading.');
      return;
    }
    const validationError = validateSelectedFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }

    const uploadToken = ++uploadTokenRef.current;
    setUploading(true);
    try {
      if (contextType !== 'issuance') {
        setError('This evidence context is read-only.');
        return;
      }
      const created = await firearmEvidenceService.uploadIssuanceEvidence(contextId, {
        file: selectedFile,
        notes,
      });
      if (uploadToken !== uploadTokenRef.current) return;
      setRecords((current) => [created, ...current.filter((record) => record.id !== created.id)]);
      setSuccess(`${created.original_name} uploaded as authorized firearm evidence.`);
      setNotes('');
      clearSelectedFile();
      onUploadComplete?.(created);
    } catch (uploadError) {
      if (uploadToken === uploadTokenRef.current) {
        setError(errorText(uploadError, 'Firearm evidence could not be uploaded.'));
      }
    } finally {
      if (uploadToken === uploadTokenRef.current) setUploading(false);
    }
  };

  const openStoredPreview = async (evidence: FirearmEvidence) => {
    const token = ++previewTokenRef.current;
    revokeStoredPreviewUrl();
    setStoredPreview({
      evidenceId: evidence.id,
      filename: evidence.original_name,
      contentType: evidence.mime_type,
      loading: true,
    });
    try {
      const blob = await firearmEvidenceService.getPreview(evidence.id);
      if (token !== previewTokenRef.current) return;
      const objectUrl = URL.createObjectURL(blob);
      storedPreviewUrlRef.current = objectUrl;
      setStoredPreview({
        evidenceId: evidence.id,
        filename: evidence.original_name,
        contentType: blob.type || evidence.mime_type,
        objectUrl,
        loading: false,
      });
    } catch (previewError) {
      if (token !== previewTokenRef.current) return;
      setStoredPreview({
        evidenceId: evidence.id,
        filename: evidence.original_name,
        contentType: evidence.mime_type,
        loading: false,
        error: errorText(previewError, 'Firearm evidence preview could not be loaded.'),
      });
    }
  };

  const downloadEvidence = async (evidence: FirearmEvidence) => {
    setDownloadingId(evidence.id);
    setError('');
    try {
      const blob = await firearmEvidenceService.downloadEvidence(evidence.id);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = safeDownloadName(evidence.original_name, evidence.id);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      setError(errorText(downloadError, 'Firearm evidence could not be downloaded.'));
    } finally {
      setDownloadingId(null);
    }
  };

  const firearmLabel = firearmSerialNumber || `${contextType === 'return' ? 'Return' : 'Issuance'} #${contextId}`;
  const panelTitle = title || (
    contextType === 'issuance' ? 'Issued Firearm Image' : 'Return-Condition Evidence'
  );
  const canUpload = !readOnly && contextType === 'issuance';

  return (
    <section className={`firearm-evidence-panel ${className}`.trim()} aria-labelledby={headingId}>
      <header className="firearm-evidence-heading">
        <div>
          <span className="firearm-evidence-eyebrow">Evidence foundation</span>
          <h2 id={headingId}>{panelTitle}</h2>
          <p>{description}</p>
        </div>
        <span className="firearm-evidence-firearm">{firearmLabel}</span>
      </header>

      {success ? <div className="success-message" role="status" aria-live="polite">{success}</div> : null}
      {error ? <div className="error-message" role="alert"><span className="error-body">{error}</span></div> : null}

      {canUpload ? (
        <form className="firearm-evidence-upload" onSubmit={uploadEvidence}>
          <div className="firearm-evidence-form-fields">
            <label>
              <span>Evidence notes</span>
              <textarea
                aria-label="Evidence notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Optional condition or handling context"
                disabled={uploading}
              />
            </label>
            <label>
              <span>Firearm image</span>
              <input
                ref={fileInputRef}
                aria-label="Firearm evidence image"
                aria-describedby={fileHelpId}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={(event) => selectFile(event.target.files?.[0] || null)}
                disabled={uploading}
              />
            </label>
            <p id={fileHelpId} className="firearm-evidence-help">
              JPEG, PNG, or WebP only. Maximum file size: 5 MiB. The server verifies the image signature and integrity hash.
            </p>
          </div>

          <div className="firearm-evidence-selection" aria-live="polite">
            {selectedFile && selectedPreviewUrl ? (
              <>
                <img src={selectedPreviewUrl} alt={`Selected ${selectedFile.name} preview`} />
                <div>
                  <strong>{selectedFile.name}</strong>
                  <span>{selectedFile.type} · {formatBytes(selectedFile.size)}</span>
                  <button className="btn btn-secondary btn-sm" type="button" onClick={clearSelectedFile} disabled={uploading}>
                    Remove selected image
                  </button>
                </div>
              </>
            ) : (
              <div className="firearm-evidence-selection-empty">
                <ImagePlus size={24} aria-hidden="true" />
                <span>Select an approved image to review its thumbnail before upload.</span>
              </div>
            )}
          </div>

          <div className="firearm-evidence-upload-actions">
            <button className="btn btn-primary" type="submit" disabled={uploading || !selectedFile}>
              <Upload size={15} aria-hidden="true" />{uploading ? 'Uploading evidence...' : 'Upload evidence'}
            </button>
          </div>
        </form>
      ) : null}

      <div className="firearm-evidence-stored" aria-busy={loading}>
        <div className="firearm-evidence-stored-heading">
          <div>
            <h3>Stored evidence</h3>
            <p>{records.length} authorized image{records.length === 1 ? '' : 's'}</p>
          </div>
          <button className="btn btn-secondary btn-sm" type="button" onClick={() => void loadEvidence()} disabled={loading}>
            <RefreshCw size={14} aria-hidden="true" />{loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loading ? (
          <div className="firearm-evidence-loading" role="status" aria-live="polite">
            <span className="loading-mark" />Loading stored firearm evidence...
          </div>
        ) : records.length === 0 ? (
          <EmptyTableState
            title={required ? 'Required evidence not uploaded' : 'No contextual evidence'}
            description={required
              ? 'This transaction does not yet have its required evidence image.'
              : 'Authorized evidence for this transaction will appear here.'}
          />
        ) : (
          <ul className="firearm-evidence-list">
            {records.map((evidence) => (
              <li key={evidence.id} className="firearm-evidence-record">
                <div className="firearm-evidence-record-main">
                  <div className="firearm-evidence-record-title">
                    <strong>{evidence.original_name}</strong>
                    <TypeBadge type={categoryLabel(evidence)} tone="blue" />
                  </div>
                  <dl>
                    <div><dt>Evidence</dt><dd>#{evidence.id}</dd></div>
                    <div><dt>Format</dt><dd>{evidence.mime_type}</dd></div>
                    <div><dt>Size</dt><dd>{formatBytes(evidence.file_size)}</dd></div>
                    {showTechnicalMetadata ? <div><dt>Dimensions</dt><dd>{evidence.width && evidence.height ? `${evidence.width} × ${evidence.height} px` : 'Not available'}</dd></div> : null}
                    <div><dt>Uploaded by</dt><dd>{evidence.uploaded_by_display || 'Authorized user'}</dd></div>
                    <div><dt>Uploaded</dt><dd>{formatDateTime(evidence.uploaded_at)}</dd></div>
                    {showTechnicalMetadata ? <div className="firearm-evidence-hash"><dt>SHA-256</dt><dd><code title={evidence.sha256_hash}>{evidence.sha256_hash}</code></dd></div> : null}
                  </dl>
                  {evidence.notes ? <p className="firearm-evidence-notes"><strong>Notes:</strong> {evidence.notes}</p> : null}
                </div>
                <div className="firearm-evidence-record-actions">
                  <button className="btn btn-secondary btn-sm" type="button" aria-label={`Preview ${evidence.original_name}`} onClick={() => void openStoredPreview(evidence)}>
                    <Eye size={14} aria-hidden="true" />Preview
                  </button>
                  {readOnly || contextType === 'issuance' || contextType === 'return' ? (
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      aria-label={`Download ${evidence.original_name}`}
                      onClick={() => void downloadEvidence(evidence)}
                      disabled={downloadingId === evidence.id}
                    >
                      <Download size={14} aria-hidden="true" />{downloadingId === evidence.id ? 'Downloading...' : 'Download'}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <DocumentPreviewModal
        open={Boolean(storedPreview)}
        title={`Evidence #${storedPreview?.evidenceId || ''} Preview`}
        filename={storedPreview?.filename}
        objectUrl={storedPreview?.objectUrl}
        contentType={storedPreview?.contentType}
        loading={storedPreview?.loading}
        error={storedPreview?.error}
        onClose={closeStoredPreview}
      />
    </section>
  );
};

export default FirearmEvidencePanel;
